import './spotlightSection.scss';
import React, {useCallback, useEffect, useState} from 'react';
import {
    useSelectedDocContext,
    useStateDocumentSelectedTags,
    useStateIsDocumentLoading,
    useStatePreviewDocumentWithHighlights
} from '../../../search-hooks';
import {DefaultRootState, useDispatch, useSelector} from 'react-redux';
import {
    resetTransformAction,
    setCurrentPageNumberAction,
    setHighlightIdAction,
    showAnnotations,
} from '../../../duck/search-actions';
import classNames from 'clsx';
import {Button, OverlayTrigger, Popover} from 'react-bootstrap';
import {SvgIcon} from '../../../../icons/svgIcon';
import {Icons} from '../../../../icons/icons';
import {useTranslation} from 'react-i18next';
import {PdfHighlight} from '../../../../common/pdf-viewer/PdfViewer.types';
import {SpotlightTagType, TagKey} from '../../../../config/config-types';
import {FilterWidget} from '../../../../common/filterWidget/filterWidget';
import {useLocalizationUtil} from '../../../../localizationUtil';
import {Spinner} from '../../../../common/spinner/spinner';

export const SpotlightSection = React.memo(() => {
    const annotations = useStateDocumentSelectedTags();
    const dispatch = useDispatch();
    const {t} = useTranslation();

    const isDocumentLoading = useStateIsDocumentLoading();
    const selectedDocument = useSelectedDocContext();
    const previewDocumentWithHighlights = useStatePreviewDocumentWithHighlights();

    const {spotlightTagTypes} = useSelector((state: DefaultRootState) => ({spotlightTagTypes: state.config.uiConfig.search.spotlightTagTypes}));

    useEffect(() => {
        if (selectedDocument) {
            dispatch(showAnnotations(true));
        }
        return () => {
            dispatch(showAnnotations(false));
        };
    }, [dispatch, selectedDocument]);

    const getTagTypeGroups = useCallback(() => {
        const tagTypeGroups = new Map<SpotlightTagType, PdfHighlight[]>();
        spotlightTagTypes.forEach((htt) => {
            const tagTypeGroup: PdfHighlight[] = [];
            annotations.forEach((annotation) => {
                if (annotation.contentsObj.str.endsWith(htt.name)) {
                    tagTypeGroup.push(annotation);
                }
            });
            if (tagTypeGroup.length > 0) tagTypeGroups.set(htt, tagTypeGroup);
        });
        return tagTypeGroups;
    }, [annotations, spotlightTagTypes]);

    const tagTypeGroups = getTagTypeGroups();

    const renderedAnnotations = useCallback(() => {
        const rendered: React.ReactNode[] = [];

        tagTypeGroups.forEach((annotations, spotlightTagType) => {
            const tagKeys = spotlightTagTypes.find((tk) => tk === spotlightTagType)?.tagKeys;

            rendered.push(
                <SpotlightSectionElement
                    spotlightTagType={spotlightTagType}
                    annotations={annotations}
                    tagKeys={tagKeys}
                />,
            );
            return rendered;
        });
        return rendered;
    }, [tagTypeGroups, spotlightTagTypes]);

    if (isDocumentLoading || !annotations || !previewDocumentWithHighlights) {
        return <Spinner/>; // if document is loading
    } else if (tagTypeGroups.size === 0) {
        return <div>{t('search.details.noAnnotations')}</div>; // after document loaded
    } else {
        return <div id={'hibu-details-selected-tags'}>{renderedAnnotations()}</div>;
    }
});

type SpotlightSectionElementProps = {
    spotlightTagType: SpotlightTagType;
    annotations: PdfHighlight[];
    tagKeys: undefined | TagKey[];
};
const SpotlightSectionElement = React.memo(({spotlightTagType, annotations, tagKeys}: SpotlightSectionElementProps) => {
    const [filter, setFilter] = useState('');
    const [expanded, setExpanded] = React.useState(true);
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const {formatNumber} = useLocalizationUtil();

    const onClickAnnotation = useCallback(
        (pageNumber: number, highlightId: string) => {
            dispatch(resetTransformAction(true));
            dispatch(setCurrentPageNumberAction(pageNumber));
            dispatch(setHighlightIdAction(highlightId));
        },
        [dispatch],
    );

    const popoverContent = useCallback(
        (annotation: PdfHighlight) => {
            if (!tagKeys) {
                return <div></div>;
            }
            const tagKey = tagKeys.find((tk) => tk.name === annotation.titleObj.str);

            if (!tagKey) {
                return <div></div>;
            }

            const name = tagKey.name;
            const popover = (
                <Popover key={'key_' + name} id={'popover-' + name}>
                    <Popover.Header as="h4">{name}</Popover.Header>
                    <Popover.Body>{tagKey?.description}</Popover.Body>
                </Popover>
            );

            return (
                <OverlayTrigger trigger="click" placement="left" overlay={popover} rootClose>
                    <Button className={classNames('tagKey-info-button', 'header-button')}>
                        <SvgIcon icon={Icons.QUESTION} />
                    </Button>
                </OverlayTrigger>
            );
        },
        [tagKeys],
    );

    const annotationPopover = useCallback(() => {
        return annotations
            ?.filter((annotation) => !filter || annotation.titleObj.str.toLowerCase().includes(filter.toLowerCase()))
            .map((annotation) => (
                <li key={'id_' + annotation.id} className={classNames('selected-tags-element')}>
                    <span
                        onClick={() => onClickAnnotation(annotation.page, annotation.id)}
                        className={'selected-tags-container'}
                    >
                        <span className={'selected-tags-title'}>{annotation.titleObj.str}</span>
                        <span className={'selected-tags-page'}>{formatNumber(annotation.page)}</span>
                    </span>
                    {popoverContent(annotation)}
                </li>
            ));
    }, [annotations, filter, formatNumber, popoverContent, onClickAnnotation]);

    function render() {
        const name = spotlightTagType.name;

        return (
            <div key={'id_' + name}>
                <div className={'selected-tags-header'}>
                    <div className={classNames('toggle-header')}>
                        <Button className={classNames('header-button', 'toggle-button')}
                                onClick={() => setExpanded(!expanded)}>
                            <SvgIcon icon={Icons.CHEVRON_DOWN} className={expanded ? 'expanded' : ''}/>
                        </Button>
                        <div className={'toggle-label'}>{t(`fieldTitle.tagsPlural.${name}.label`)}</div>
                    </div>
                    {expanded && (
                        <FilterWidget
                            filter={filter}
                            setFilter={setFilter}
                            title={t(`fieldTitle.tagsPlural.${name}.label`)}
                        />
                    )}
                </div>
                {expanded && <ul className={classNames('selected-tags-list')}>{annotationPopover()}</ul>}
            </div>
        );
    }

    return render();
});
